import { type CSSProperties } from 'react'
import { css, type SerializedStyles } from '@emotion/react'
import { type Theme } from '@mui/material/styles'
import { rem, size } from 'polished'

import { typographyOverrides } from '@Material/typography'
import { highContrast } from '@Styles/a11y'
import { RADIUS_1 } from '@Styles/constants/radius'
import { getFocusVisibleStyle } from '@Styles/focus'

import { type ButtonSize, type ButtonVariant, type IconPosition } from './Buttons.types'

export const buttonIcon = (buttonSize: ButtonSize): SerializedStyles =>
  css({
    fill: 'currentColor',
    ...size(rem(buttonSize === 'small' ? 20 : 24)),
  })

const buttonContainer = css({
  maxWidth: rem(288),
  minHeight: rem(48),
  minWidth: rem(156),
})

export const button = (theme: Theme, variant: ButtonVariant): SerializedStyles =>
  css(
    highContrast({
      border: 'solid 2px transparent',
    }),
    {
      borderRadius: RADIUS_1,
      padding: theme.spacing(1, 3),
      textTransform: 'none',
      whiteSpace: 'break-spaces',
      ...(typographyOverrides(theme)?.body1 as CSSProperties),
      '&:focus-visible::before': getFocusVisibleStyle(
        variant === 'primary' ? theme.palette.primary.contrastText : theme.palette.primary.main,
        RADIUS_1,
      ),
      '&:hover, &:focus:not(:focus-visible)': {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
      },
      ...(variant === 'secondary' && {
        borderColor: theme.palette.primary.main,
      }),
    },
    buttonContainer,
  )

export const smallButton = (theme: Theme): SerializedStyles =>
  css({
    ...(typographyOverrides(theme)?.subtitle1 as CSSProperties),
    minHeight: rem(40),
  })

export const buttonLinkCard = (theme: Theme, color: string = theme.palette.primary.main): SerializedStyles =>
  css({
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.customPalette.onElements.selected,
    },
    alignItems: 'center',
    color,
    display: 'flex',
    justifyContent: 'center',
  })

export const buttonLinkCardIcon = (
  theme: Theme,
  color: string = theme.palette.primary.main,
  position: IconPosition = 'left',
): SerializedStyles =>
  css({
    color,
    [position === 'left' ? 'marginRight' : 'marginLeft']: theme.spacing(1),
    ...size(rem(16)),
  })

export const loadingWrapper = css(
  {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: rem(160),
  },
  buttonContainer,
)

export const fullWidth = css({
  width: '100%',
})
